import { computed, Signal } from '@angular/core';

import { DateService } from '../services/date.service';

export const defaultDiseaseWidgetDateRange = {
  daysInPast: 1,
  daysInFuture: 1,
};

export const getDefaultDiseaseWidgetSelectedDates = (dateService: DateService, cropYear?: number): Date[] =>
  cropYear === undefined
    ? dateService.generateDateRange(defaultDiseaseWidgetDateRange.daysInPast, defaultDiseaseWidgetDateRange.daysInFuture)
    : dateService.generateDateRange(
        defaultDiseaseWidgetDateRange.daysInPast,
        defaultDiseaseWidgetDateRange.daysInFuture,
        cropYear,
      );

export const areDateRangesSameDay = (dateService: DateService, first: Date[], second: Date[]): boolean =>
  first.length === second.length &&
  first.every((date, index) => dateService.isDateSameDay(date, second[index]));

/**
 * Returns a computed signal that indicates whether navigating to the previous date range is disabled.
 * Navigation is disabled when there is no min date, no dates selected, or the first date is at/before the min date.
 *
 * @param dates - Signal providing the currently selected dates.
 * @param minDate - Signal providing the minimum allowed date.
 * @param dateService - DateService for date comparison.
 * @returns Signal<boolean> - True when previous navigation should be disabled.
 */
export function isPreviousDateNavigationDisabled(
  dates: Signal<Date[]>,
  minDate: Signal<Date | undefined>,
  dateService: DateService,
): Signal<boolean> {
  return computed(() => {
    const currentDates = dates();
    const min = minDate();
    return !min || currentDates.length === 0 || dateService.isDateEarlierThanOrEquals(currentDates[0], min);
  });
}

/**
 * Returns a computed signal that indicates whether navigating to the next date range is disabled.
 * Navigation is disabled when there is no max date, no dates selected, or the last date is at/past the max date.
 *
 * @param dates - Signal providing the currently selected dates.
 * @param maxDate - The maximum allowed date.
 * @param dateService - DateService for date comparison.
 * @returns Signal<boolean> - True when next navigation should be disabled.
 */
export function isNextDateNavigationDisabled(
  dates: Signal<Date[]>,
  maxDate: Date,
  dateService: DateService,
): Signal<boolean> {
  return computed(() => {
    const currentDates = dates();
    return currentDates.length === 0 || dateService.isDateLaterThanOrEquals(currentDates[currentDates.length - 1], maxDate);
  });
}

/**
 * Represents the date state used for date range navigation.
 */
export interface DateNavigationState {
  selectedDates: Date[];
  startDatePicker: Date | null;
  endDatePicker: Date | null;
}

/**
 * Computes the new date navigation state after shifting by one day.
 * Moves selectedDates, startDatePicker, and endDatePicker by ±1 day.
 *
 * @param state - The current date navigation state.
 * @param direction - The direction to navigate (-1 for previous, 1 for next).
 * @param dateService - DateService for date arithmetic.
 * @returns DateNavigationState - The new state after navigation.
 */
export function navigateDatesByOneDay(
  state: DateNavigationState,
  direction: -1 | 1,
  dateService: DateService,
): DateNavigationState {
  const shiftDate = (date: Date): Date => dateService.moveDateRange([date], direction)[0];
  return {
    selectedDates: dateService.moveDateRange(state.selectedDates, direction),
    startDatePicker: state.startDatePicker ? shiftDate(state.startDatePicker) : null,
    endDatePicker: state.endDatePicker ? shiftDate(state.endDatePicker) : null,
  };
}
